import React from 'react';
import InfoModal from '@/components/molecules/InfoModal';
import RCCreationForm from '@/components/organisms/RCCreationForm';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { revenueCategoryService } from '@/services/revenue-category.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { budgetService } from '@/services/budget.service';
import { RevenueCategoryFormValues } from '@/services/types/revenue-category.types';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const RCEditPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxRevenueValue, setMaxRevenueValue] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [revenueCategory, setRevenueCategory] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { budgetId, rcId } = useParams();
  const location = useLocation();

  const rcData = location.state?.rcData as any | undefined;

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/revenue-categories/${rcId}`,
      // This page is always pushed from the RC detail page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  const formDefaultValues = useMemo(
    () => ({
      name: revenueCategory?.name,
      description: revenueCategory?.description,
      rcrt: revenueCategory?.rcrt,
      rcrt_alert_threshold: revenueCategory?.rcrt_alert_threshold
    }),
    [revenueCategory]
  );

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: 'Revenue Categories',
          path: `/budgets/${budgetId}/revenue-categories`
        },
        {
          title: rcData?.name ? rcData?.name : 'Revenue Category Detail',
          path: `/budgets/${budgetId}/revenue-categories/${rcId}`
        },
        {
          title: 'Edit',
          path: ``
        }
      ])
    );
  }, [dispatch, budgetId, rcData?.name, rcId]);

  useEffect(() => {
    getRevenueCategory();
    fetchBudgetDetails();
  }, []);

  const fetchBudgetDetails = async () => {
    if (!budgetId) {
      setError('Budget ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data } = await budgetService.getBudget(budgetId);

      // For revenue categories, the max revenue value should be the remaining BTRT plus current RCRT
      const currentRcrt = revenueCategory?.rcrt || 0;
      setMaxRevenueValue(data.btrt - (data?.btrt_achieved || 0) + currentRcrt);
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget details');
    } finally {
      setLoading(false);
    }
  };

  const getRevenueCategory = async () => {
    try {
      const { data: rcData } = await revenueCategoryService.getRevenueCategory(budgetId || '', rcId || '');
      setRevenueCategory(rcData);
      setMaxRevenueValue(rcData.remaining_rcrt);
    } catch (error) {
      console.error('Error getting revenue category:', error);
    }
  };

  const handleSubmit = async (data: RevenueCategoryFormValues) => {
    console.log('Submit', data);
    try {
      if (!budgetId) {
        throw new Error('BudgetId is required');
      }
      const response = await revenueCategoryService.updateRevenueCategory(budgetId, rcId || '', {
        name: data.name,
        description: data.description,
        rcrt: Number(data.rcrt),
        rcrt_alert_threshold: Number(data.rcrt_alert_threshold)
      });
      console.log('Response', response);
      toast.success('Revenue category updated successfully');
      allowNavigation();
      // This page was pushed from the RC detail page, so pop back to it
      // instead of replacing — replacing to the same URL as the previous
      // history entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error) {
      console.error('Error updating revenue category:', error);
      toast.error('Failed to update revenue category');
    }
  };

  return (
    <>
      <MobileAppBar title="Revenue Category Edit" />

      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center w-[80%] mx-auto md:w-full uppercase md:capitalize font-semibold mb-5 md:text-left md:text-[24px] md:mb-6">
          Edit your Revenue Category
        </h2>

        {/* FORM */}
        <RCCreationForm
          onSubmit={handleSubmit}
          defaultValues={formDefaultValues}
          isEditMode={true}
          maxRevenueValue={maxRevenueValue}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          onCancel={handleCancel}
        />
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your revenue category"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Revenue Category Updated"
          message={<p>You have successfully updated the Revenue Category!</p>}
          buttonText="Go to Revenue Categories"
          onButtonClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
        />
      )}
    </>
  );
};

export default RCEditPage;
